import React from "react";

export default function AppModal({
  open,
  onClose,
  title,
  children,
  footer = null,
  maxWidthClass = "max-w-5xl",
  closeOnBackdrop = true,
  initialFocusRef = null,
}) {
  const [mounted, setMounted] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const DURATION = 220; // ms

  // 닫기 핸들러 (애니메이션 후 onClose 호출)
  const handleClose = React.useCallback(() => {
    setClosing(true);
    setMounted(false);
    setTimeout(() => {
      setClosing(false);
      onClose?.();
    }, DURATION);
  }, [onClose]);

  // body 스크롤 잠금 + enter 애니메이션 시작
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const raf = requestAnimationFrame(() => setMounted(true));

    // 처음 열릴 때 포커스
    const t = setTimeout(() => {
      if (initialFocusRef?.current) initialFocusRef.current.focus?.();
    }, 0);

    return () => {
      document.body.style.overflow = prev;
      cancelAnimationFrame(raf);
      clearTimeout(t);
      setMounted(false);
      setClosing(false);
    };
  }, [open, initialFocusRef]);

  // ESC로 닫기  // ⬅ deps에 handleClose 추가
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  // 닫히는 애니메이션 중엔 렌더 유지
  if (!open && !closing) return null;

  const backdropClass = mounted && !closing ? "opacity-100" : "opacity-0";
  const panelClass =
    mounted && !closing
      ? "opacity-100 translate-y-0 sm:scale-100"
      : "opacity-0 translate-y-3 sm:translate-y-0 sm:scale-[0.98]";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop (fade) */}
      <div
        className={[
          "absolute inset-0 bg-black/50 backdrop-blur-sm",
          "transition-opacity duration-200 ease-out",
          backdropClass,
        ].join(" ")}
        onClick={closeOnBackdrop ? handleClose : undefined}
        aria-hidden="true"
      />
      {/* Panel (fade + translate/scale) */}
      <div
        role="dialog"
        aria-modal="true"
        className={[
          "relative z-10 w-[90vw] sm:w-full",
          maxWidthClass,
          "rounded-lg border border-zinc-950/10 bg-white shadow-xl",
          "dark:bg-zinc-900 dark:border-zinc-300/10",
          "transition-all duration-200 ease-out",
          panelClass,
        ].join(" ")}
      >
        {/* Header */}
        {title !== undefined && (
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-zinc-200 dark:border-zinc-800">
            <h3 className="text-lg/8 font-semibold text-zinc-950 dark:text-white">
              {title}
            </h3>
            <button
              type="button"
              className="px-2 py-1 text-sm rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-700 dark:text-zinc-200"
              onClick={handleClose}
            >
              닫기
            </button>
          </div>
        )}

        {/* Body */}
        <div className="p-5">{children}</div>

        {/* Footer (옵션) */}
        {footer !== null && <div className="px-5 pb-5">{footer}</div>}
      </div>
    </div>
  );
}
